// Question Extraction observability — Create Assignment from PDF/Word.
//
// Reads the DEDICATED `question-extraction-runs` container, not `async-jobs`.
// The job record is a generic execution trail with a 7-day TTL that knows
// nothing about what an extraction produced; the run record is the durable
// answer to "what did this import read and what did it yield", and it survives
// the job. See docs/FeatureDesigns/operation-run-records.md.
//
// The counters are the point of this view. Enrichment degrades per batch by
// design so one bad batch cannot sink an import — which means a run that
// authored answers for NOTHING still finishes as `succeeded`. "Enriched 0/16"
// here is the only place that surfaces before a teacher opens the assignment
// and finds questions with no answer and no solution.
import { useMemo, useState, type MouseEvent } from 'react';
import { useOpenOperationRow } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  succeeded: 'success', failed: 'error', running: 'warning',
};

type Run = NonNullable<Awaited<ReturnType<typeof adminApi.getQuestionExtractionRuns>>>['items'][number];

export function ExtractionList() {
  const [status, setStatus] = useState('');
  const [sourceKind, setSourceKind] = useState('');
  const openRow = useOpenOperationRow();

  const { data, isLoading, error } = useQuery({
    queryKey: ['questionExtractionRuns', status, sourceKind],
    queryFn: () => adminApi.getQuestionExtractionRuns({
      status: status || undefined,
      sourceKind: sourceKind || undefined,
      limit: 50,
    }),
  });

  const rows = data?.items ?? [];

  // Stop row navigation when an in-cell link is clicked (links open their own tab).
  const stop = (e: MouseEvent) => e.stopPropagation();

  const columns = useMemo<ColumnDef<Run>[]>(() => [
    { key: 'started', header: 'Started', render: (r) => format(new Date(r.startedAt), 'MMM d, HH:mm:ss') },
    {
      key: 'teacher', header: 'Teacher', render: (r) => (
        <div>
          <div>{r.teacherName || '—'}</div>
          {r.teacherEmail && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.teacherEmail}</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={STATUS_BADGE[r.status] ?? 'default'} size="sm">{r.status}</Badge> },
    { key: 'source', header: 'Source', render: (r) => (
      <div>
        <div style={{ textTransform: 'uppercase', fontSize: 11, color: 'var(--text-tertiary)' }}>{r.sourceKind}</div>
        {r.sourceFileUrl
          ? <a href={r.sourceFileUrl} target="_blank" rel="noopener noreferrer" onClick={stop}>{r.sourceFileName || 'file'} ↗</a>
          : (r.sourceFileName || '—')}
      </div>
    ) },
    {
      key: 'pages', header: 'Pages', render: (r) => {
        if (!r.pages) return '—';
        return r.pages.dropped > 0
          ? <span className="dc-error" title={`${r.pages.dropped} page${r.pages.dropped === 1 ? '' : 's'} over the cap were not read`}>{r.pages.read} (+{r.pages.dropped} dropped)</span>
          : String(r.pages.read);
      },
    },
    { key: 'latency', header: 'Latency', render: (r) => (r.latencyMs != null ? `${(r.latencyMs / 1000).toFixed(1)}s` : '—') },
    {
      key: 'extracted', header: 'Extracted', render: (r) => {
        const n = r.createdQuestionIds?.length ?? r.extracted;
        if (n == null) return '—';
        return n > 0
          ? <a href={`/questions?batch=${r.asyncJobId ?? r.id}&source=teacher`} target="_blank" rel="noopener noreferrer" onClick={stop}>{n} ↗</a>
          : '0';
      },
    },
    { key: 'diagrams', header: 'Diagrams', render: (r) => (r.withDiagram == null ? '—' : String(r.withDiagram)) },
    {
      // Badged whenever ANY question missed enrichment — those reach the
      // teacher with no answer and no solution.
      key: 'enriched', header: 'Enriched', render: (r) => {
        if (r.enriched == null || r.extracted == null) return '—';
        const label = `${r.enriched}/${r.extracted}`;
        if ((r.enrichmentFailed ?? 0) === 0) return label;
        return <Badge variant={r.enriched === 0 ? 'error' : 'warning'} size="sm">{label}</Badge>;
      },
    },
    {
      key: 'cost', header: 'Tokens / Cost', render: (r) => (r.llm
        ? (
          <div style={{ fontSize: 12 }}>
            <div>{r.llm.inputTokens.toLocaleString()} in / {r.llm.outputTokens.toLocaleString()} out</div>
            {r.llm.costUsd != null && <div style={{ color: 'var(--text-tertiary)' }}>${r.llm.costUsd.toFixed(4)}</div>}
          </div>
        )
        : '—'),
    },
    { key: 'error', header: 'Error', render: (r) => (r.error ? <span className="dc-error" title={r.error}>{r.error.slice(0, 80)}</span> : '—') },
  ], []);

  const toolbar = (
    <>
      <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="">All Statuses</option>
        <option value="succeeded">Succeeded</option>
        <option value="running">Running</option>
        <option value="failed">Failed</option>
      </select>
      {/* PDF goes through the vision pipeline, Word through the text pipeline —
          different prompts, different failure modes, worth isolating. */}
      <select className="filter-select" value={sourceKind} onChange={(e) => setSourceKind(e.target.value)}>
        <option value="">All Sources</option>
        <option value="pdf">PDF</option>
        <option value="docx">Word (.docx)</option>
      </select>
    </>
  );

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={(r) => r.id}
      onRowClick={(r) => openRow(rows, r, '/extraction')}
      toolbar={toolbar}
      loading={isLoading}
      emptyMessage="No question-extraction runs yet."
    />
  );
}
